import { type FC, type ReactNode, type RefObject, useEffect } from "react";

import { type ProjectReportPagesQuery } from "@app_schema";

import { PageFrame } from "@styled/page_frame";
import { ReportPagesColumn } from "@styled/report_pages_bar";
import { PaneSticky } from "@styled/review_pane";

import { type PageTarget } from "./page_tag";
import { type ChatReads } from "./use_assistant_chats";

type ReportPages = NonNullable<
  ProjectReportPagesQuery["project"]["document"]
>["pages"];

const frameId = (documentId: string, page: number) =>
  `report-page-${documentId}-${String(page)}`;

const scrollToFrame = (documentId: string, page: number | null) => {
  if (page === null) return;

  document
    .getElementById(frameId(documentId, page))
    ?.scrollIntoView({ block: "start" });
};

// The report itself, pages scrolling. A page tag scrolls to its page and
// rings it in teal. While the assistant answers, the pages follow what it
// reads or its search found (violet), unless `follow` is off, and what it read
// keeps an edge.
export const ReportPages: FC<{
  documentId: string;
  pages: ReportPages;
  target: PageTarget | null;
  reads: ChatReads;
  line?: ReactNode;
  scrollRef: RefObject<HTMLDivElement | null>;
  hidden?: boolean;
  follow?: boolean;
}> = ({
  documentId,
  pages,
  target,
  reads,
  line,
  scrollRef,
  hidden,
  follow = true,
}) => {
  const targetPage = target?.documentId === documentId ? target.page : null;
  const reading =
    reads.readingNow?.documentId === documentId ? reads.readingNow.pages : [];
  const found =
    reads.searchingNow?.pages?.find((hits) => hits.documentId === documentId)
      ?.numbers ?? [];
  const readHere = reads.pages.get(documentId);

  useEffect(
    () => scrollToFrame(documentId, targetPage),
    [documentId, targetPage, pages.length],
  );
  const followed = follow ? (reading[0] ?? found[0] ?? null) : null;
  useEffect(
    () => scrollToFrame(documentId, followed),
    [documentId, followed, pages.length],
  );

  const markOf = (page: number) => {
    if (reading.includes(page)) return "reading";
    if (found.includes(page)) return "found";
    if (readHere?.has(page)) return "read";
    return undefined;
  };

  return (
    <ReportPagesColumn scrollRef={scrollRef} hidden={hidden}>
      {line && <PaneSticky background="slate-100">{line}</PaneSticky>}
      {pages.map((page) => (
        <PageFrame
          key={page.id}
          id={frameId(documentId, page.number)}
          number={page.number}
          url={page.file.url}
          width={page.width}
          height={page.height}
          targeted={page.number === targetPage}
          mark={markOf(page.number)}
        />
      ))}
    </ReportPagesColumn>
  );
};
